import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAgentRatings } from "@/features/agents/hooks/useAgents";

interface Props {
  agentId: string;
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={
            i < rating
              ? "h-3 w-3 fill-yellow-500 text-yellow-500"
              : "h-3 w-3 text-muted-foreground"
          }
        />
      ))}
    </div>
  );
}

export function RatingsTab({ agentId }: Props) {
  const { t } = useTranslation("agents");
  const { data, isLoading } = useAgentRatings(agentId);

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">Carregando avaliacoes...</p>
    );
  }

  if (!data || data.count === 0) {
    return <p className="text-sm text-muted-foreground">{t("ratings.empty")}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/30 p-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl font-semibold">
            {data.avg?.toFixed(1) ?? "—"}
          </span>
          <div>
            <StarRow rating={Math.round(data.avg ?? 0)} />
            <p className="text-xs text-muted-foreground">
              {t("metrics.ratingCount", { count: data.count })}
            </p>
          </div>
        </div>
      </div>

      <ul className="space-y-3">
        {data.items.map((r) => (
          <li
            key={`${r.agent_id}-${r.user_id}`}
            className="rounded-md border p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <span className="text-sm font-medium">{r.user_name}</span>
                <p className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString("pt-BR")}
                </p>
              </div>
              <StarRow rating={r.rating} />
            </div>
            {r.comment ? (
              <p className="mt-2 text-sm whitespace-pre-wrap">{r.comment}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
